import { useNavigate, useParams } from '@tanstack/react-router';
import MarkdownEditor, { README_EDITOR_OPTIONS } from '_/common/components/MarkdownEditor';
import { PROJECT_ENVIRONMENT_OPTIONS } from '_/constants/common';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useProjectReadme, useUpdateProject, useUpdateProjectReadme } from '_/hooks/useProjects';
import { Form, Input, Select, Button, Space, Alert, notification } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';

const EditProject = (): ReactElement => {
  const [form] = Form.useForm();
  const history = useNavigate();
  const params = useParams({ strict: false }) as Partial<{ orgSlug: string; projectSlug: string }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const { setBreadcrumbs } = useTopHeader();

  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject } = useProject();
  const { data: selectedProjectReadme = '', isLoading: isLoadingReadme } = useProjectReadme(selectedProject?.id);

  const { mutateAsync: updateProjectMutation, isPending: isUpdatingProject } = useUpdateProject(projectSlug, selectedProject?.id);
  const { mutateAsync: updateProjectReadmeMutation, isPending: isUpdatingReadme } = useUpdateProjectReadme(
    projectSlug,
    selectedProject?.id,
  );

  const [projectReadme, setProjectReadme] = useState<string>('');
  const [saveProjectErrors, setSaveProjectErrors] = useState<any[]>([]);
  const [initialReadme, setInitialReadme] = useState<string>('');

  const isUpdating = isUpdatingProject || isUpdatingReadme;

  // Set up header with breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${projectSlug}/home`,
      },
      { title: 'Edit' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization, selectedOrgSlug, selectedProject, projectSlug]);

  // Initialize form when selectedProject and readme are loaded
  useEffect(() => {
    if (selectedProject && selectedProjectReadme !== undefined) {
      form.setFieldsValue({
        name: selectedProject.name,
        slug: selectedProject.slug,
        environment: selectedProject.environment,
      });
      setProjectReadme(selectedProjectReadme);
      setInitialReadme(selectedProjectReadme);
    }
  }, [selectedProject, selectedProjectReadme, form]);

  const onFinishFailed = () => {};

  const onFinish = async (values: any) => {
    setSaveProjectErrors([]);

    if (!selectedProject?.id) {
      console.error('No selectedProject available');
      setSaveProjectErrors([{ type: 'api', error: 'Project not loaded. Please refresh the page.' }]);
      return;
    }

    try {
      // Update project metadata (name, environment)
      await updateProjectMutation({
        projectId: selectedProject.id,
        project: {
          name: values.name,
          environment: values.environment,
        },
      });

      // Update readme if changed
      if (initialReadme !== projectReadme) {
        await updateProjectReadmeMutation(projectReadme);
      }

      notification.success({
        message: 'Project Saved',
      });
      void history({ to: `/orgs/${orgSlug}/projects/${projectSlug}` });
    } catch (error: any) {
      console.error('Error updating project:', error);
      setSaveProjectErrors(error?.messages || [{ type: 'api', error: error?.message || 'Unknown error' }]);
    }
  };

  const onCancel = () => {
    void history({ to: `/orgs/${orgSlug}/projects/${projectSlug}` });
  };

  const onReadmeChange = (value: string) => {
    setProjectReadme(value);
  };

  // Filter errors by type
  const nameErrors = saveProjectErrors.filter((e: any) => e.type === 'name');
  const apiErrors = saveProjectErrors.filter((e: any) => e.type === 'api');

  if (isLoadingReadme || !selectedProject) {
    return null;
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {apiErrors.length > 0 && (
        <Alert
          message="Error"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {apiErrors.map((error: any, idx: number) => (
                <li key={idx}>{error.error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          name: '',
          slug: '',
          environment: 'PRODUCTION',
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please input the project name!' }]}
          validateStatus={nameErrors.length > 0 ? 'error' : undefined}
          help={nameErrors.length > 0 ? nameErrors[0].error : undefined}
        >
          <Input placeholder="Project name" />
        </Form.Item>

        <Form.Item label="Slug / ID - CANNOT BE CHANGED AFTER CREATION" name="slug">
          <Input placeholder="project-slug" disabled />
        </Form.Item>

        <Form.Item label="Environment" name="environment" rules={[{ required: true, message: 'Please select an environment!' }]}>
          <Select placeholder="Select Environment">
            {PROJECT_ENVIRONMENT_OPTIONS.map((env: string) => (
              <Select.Option value={env} key={env}>
                {env}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ marginBottom: 55, marginTop: 25, position: 'relative' }}>
          <MarkdownEditor editorOptions={README_EDITOR_OPTIONS} onChange={onReadmeChange} value={projectReadme} />
          {!projectReadme && <div style={{ color: '#ff4d4f', fontSize: 14, marginTop: 4 }}>README is required</div>}
        </div>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={isUpdating}>
              Save
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EditProject;
